import { useEffect, useMemo } from 'react';
import { usePrevious } from '@fortress-technology-solutions/fortress-component-library/hooks';
import useReactHookFormProps from '../../../../../hooks/useReactHookFormProps';
import { formatDateDB } from '@fortress-technology-solutions/fortress-component-library/utils';
import moment from 'moment/moment';
import { FIELDS } from './constants';
import isEqual from 'lodash/isEqual';

const getDefaultValues = (values) => {
  return {
    HOMEAffordabilityPeriodStartDate: values?.HOMEAffordabilityPeriodStartDate
      ? moment(values.HOMEAffordabilityPeriodStartDate)
      : '',
    HOMEUnitType: values?.HOMEUnitType || '',
    HOMEAnnualIncomeType: values?.HOMEAnnualIncomeType || '',
    HOMEPropertyType: values?.HOMEPropertyType || '',
  };
};

const normalizeEmptyStringToNull = (value) => (value === '' ? null : value);

export const useHOME = ({ editMode, values, onChange }) => {
  const prevValues = usePrevious(values);
  const hasValuesChanged = !isEqual(prevValues, values);
  const ReactHookFormProps = useReactHookFormProps({
    defaultValues: getDefaultValues(values),
  });

  const memoizedFields = useMemo(() => {
    const fieldsCopy = [...FIELDS];

    return fieldsCopy.map((field) => ({
      ...field,
      readOnly: !editMode,
      placeholder: editMode ? undefined : field.placeholder,
    }));
  }, [editMode]);

  const { reset, watch } = ReactHookFormProps;

  useEffect(() => {
    if (!hasValuesChanged) return;
    reset(getDefaultValues(values));
  }, [hasValuesChanged, reset, values]);

  const HOMEAffordabilityPeriodStartDate = watch(
    'HOMEAffordabilityPeriodStartDate',
  );
  useEffect(() => {
    const formattedHomeAffordabilityStartDate =
      formatDateDB(HOMEAffordabilityPeriodStartDate) || null;

    onChange({
      target: {
        name: 'setup.HOMEAffordabilityPeriodStartDate',
        value: formattedHomeAffordabilityStartDate,
      },
    });
  }, [HOMEAffordabilityPeriodStartDate, onChange]);

  const HOMEUnitType = watch('HOMEUnitType');
  useEffect(() => {
    const normalizedHomeUnitType = normalizeEmptyStringToNull(HOMEUnitType);

    onChange({
      target: {
        name: 'setup.HOMEUnitType',
        value: normalizedHomeUnitType,
      },
    });
  }, [HOMEUnitType, onChange]);

  const HOMEAnnualIncomeType = watch('HOMEAnnualIncomeType');
  useEffect(() => {
    const normalizedHomeAnnualIncomeType =
      normalizeEmptyStringToNull(HOMEAnnualIncomeType);

    onChange({
      target: {
        name: 'setup.HOMEAnnualIncomeType',
        value: normalizedHomeAnnualIncomeType,
      },
    });
  }, [HOMEAnnualIncomeType, onChange]);

  const HOMEPropertyType = watch('HOMEPropertyType');
  useEffect(() => {
    const normalizedHomePropertyType =
      normalizeEmptyStringToNull(HOMEPropertyType);

    onChange({
      target: {
        name: 'setup.HOMEPropertyType',
        value: normalizedHomePropertyType,
      },
    });
  }, [HOMEPropertyType, onChange]);

  return {
    ReactHookFormProps,
    memoizedFields,
  };
};
